import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import { toNumber } from '../ui/format'
import { JournalAmend } from '../ui/JournalAmend'
import {
  Banner, Button, Empty, ErrorBanner, Field, NumberInput, Panel, Select, Spinner, TextInput,
} from '@shop/ui'

type Account = {
  id: string
  code: string
  nameAr: string
  kind: string
  normalSide: number
  isSystem: boolean
  isActive: boolean
}

type TrialLine = {
  code: string
  nameAr: string
  kind: string
  debit: number
  credit: number
  balance: number
}

type TrialBalance = {
  from: string
  to: string
  totalDebit: number
  totalCredit: number
  lines: TrialLine[]
}

type Income = {
  revenue: number
  expense: number
  net: number
  revenueLines: TrialLine[]
  expenseLines: TrialLine[]
}

type JournalLine = {
  lineNo: number
  accountCode: string
  accountName: string
  debit: number
  credit: number
  memo: string | null
  partyKind: string | null
  partyId: string | null
}

type Journal = {
  id: string
  journalNo: string
  entryDate: string
  sourceType: string
  memo: string | null
  reversalOfId: string | null
  total: number
  lines: JournalLine[]
  isReversed: boolean
}

const SOURCE_NAMES: Record<string, string> = {
  SALE: 'بيع',
  SALE_RETURN: 'مرتجع',
  REFUND: 'ردُّ مال',
  RECEIPT: 'استلام',
  COLLECTION: 'سندُ قبض',
  ADJUSTMENT: 'تسوية',
  MANUAL: 'قيدٌ يدويّ',
  REVERSAL: 'عكس',
}

const money = (value: number) => value.toFixed(3)

const today = () => new Date().toISOString().slice(0, 10)

const monthStart = () => `${new Date().toISOString().slice(0, 7)}-01`

/**
 * الدفاترُ — <b>ميزانُ المراجعة وقائمةُ الدخل والقيود</b>.
 *
 * <p>
 * ⚠ <b>ولا تُخلط قائمةُ الدخل بتقرير الربح.</b> ذاك يقرأ المبيعاتِ وكلفتَها،
 * <b>وهذه تقرأ ما رُحّل</b> — ويشمل الإيجارَ والرواتبَ وفروقَ الجرد.
 * واختلافُهما ليس عطبًا: الأوّلُ هامشُ البضاعة، والثاني ربحُ المحلّ.
 * </p>
 */
export default function Accounting() {
  const { can } = useAuth()
  const [from, setFrom] = useState(monthStart())
  const [to, setTo] = useState(today())
  const [tab, setTab] = useState<'balance' | 'income' | 'journals'>('balance')

  if (!can(Perm.ledgerView)) {
    return (
      <Panel title="الدفاتر">
        <Empty>لا تملك عرضَ الدفاتر.</Empty>
      </Panel>
    )
  }

  const range = `from=${from}&to=${to}`

  return (
    <div className="space-y-4">
      <Panel title="الدفاتر">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="من">
            <TextInput value={from} onChange={setFrom} type="date" dir="ltr" />
          </Field>

          <Field label="إلى">
            <TextInput value={to} onChange={setTo} type="date" dir="ltr" />
          </Field>

          <Field label="العرض">
            <Select value={tab} onChange={(v) => setTab(v as typeof tab)}>
              <option value="balance">ميزانُ المراجعة</option>
              <option value="income">قائمةُ الدخل</option>
              <option value="journals">القيود</option>
            </Select>
          </Field>
        </div>
      </Panel>

      {tab === 'balance' && <TrialBalancePanel range={range} />}
      {tab === 'income' && <IncomePanel range={range} />}
      {tab === 'journals' && <JournalsPanel range={range} />}

      {can(Perm.journalPost) && <ManualEntry />}
    </div>
  )
}

/** اسمُ حسابٍ يُضغط فيفتح كشفَه — طلبُ المالك ٢٠٢٦-١٠-٠٨. */
function AccountLink({ code, name }: { code: string; name: string }) {
  const navigate = useNavigate()

  return (
    <button
      type="button"
      className="text-start text-brand underline-offset-2 hover:underline"
      onClick={() => navigate(`/statement?tab=accounts&code=${code}`)}
    >
      {name}
    </button>
  )
}

function TrialBalancePanel({ range }: { range: string }) {
  const balance = useQuery({
    queryKey: ['trial-balance', range],
    queryFn: () => api<TrialBalance>(`/api/v1/accounting/trial-balance?${range}`),
  })

  if (balance.isPending) {
    return <Spinner />
  }

  if (balance.isError) {
    return <ErrorBanner error={balance.error} />
  }

  const data = balance.data!
  const balanced = Math.abs(data.totalDebit - data.totalCredit) < 0.0005

  return (
    <Panel title="ميزانُ المراجعة">
      {/*
        ⚠ **والتوازنُ يُقال لا يُفترض**: ميزانٌ مختلٌّ يعني قيدًا معطوبًا،
          وإخفاؤه يجعل كلَّ رقمٍ بعده موضعَ شكّ.
      */}
      <Banner tone={balanced ? 'ok' : 'danger'}>
        {balanced
          ? `متوازن: المدينُ والدائنُ ${money(data.totalDebit)}`
          : `⚠ غيرُ متوازن: المدينُ ${money(data.totalDebit)} والدائنُ ${money(data.totalCredit)}`}
      </Banner>

      {data.lines.length === 0 ? (
        <div className="mt-3"><Empty>لا قيودَ في هذا المدى.</Empty></div>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-ink-soft">
              <tr>
                <th className="p-1 text-start">الرمز</th>
                <th className="p-1 text-start">الحساب</th>
                <th className="p-1 text-end">مدين</th>
                <th className="p-1 text-end">دائن</th>
                <th className="p-1 text-end">الرصيد</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((line) => (
                <tr key={line.code} className="border-t border-line">
                  <td className="tabular p-1" dir="ltr">{line.code}</td>
                  <td className="p-1"><AccountLink code={line.code} name={line.nameAr} /></td>
                  <td className="tabular p-1 text-end" dir="ltr">{money(line.debit)}</td>
                  <td className="tabular p-1 text-end" dir="ltr">{money(line.credit)}</td>
                  <td className="tabular p-1 text-end font-medium" dir="ltr">
                    {money(line.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}

function IncomePanel({ range }: { range: string }) {
  const income = useQuery({
    queryKey: ['income', range],
    queryFn: () => api<Income>(`/api/v1/accounting/income-statement?${range}`),
  })

  if (income.isPending) {
    return <Spinner />
  }

  if (income.isError) {
    return <ErrorBanner error={income.error} />
  }

  const data = income.data!

  return (
    <Panel title="قائمةُ الدخل">
      <div className="grid gap-2 sm:grid-cols-3">
        <Figure label="الإيرادات" value={money(data.revenue)} />
        <Figure label="المصاريف" value={money(data.expense)} />
        <Figure
          label={data.net >= 0 ? 'الربح' : 'الخسارة'}
          value={money(Math.abs(data.net))}
          tone={data.net >= 0 ? 'ok' : 'warn'}
        />
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Section title="الإيرادات" lines={data.revenueLines} />
        <Section title="المصاريف" lines={data.expenseLines} />
      </div>

      <p className="mt-3 text-xs text-ink-soft">
        ⚠ وهذا ربحُ المحلّ لا هامشُ البضاعة — يشمل الإيجارَ والرواتبَ وفروقَ
        الجرد. وتقريرُ الربح في «التقارير» يقيس هامشَ البيع وحدَه.
      </p>
    </Panel>
  )
}

function Section({ title, lines }: { title: string; lines: TrialLine[] }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-medium text-ink-soft">{title}</h3>

      {lines.length === 0 ? (
        <Empty>لا شيء.</Empty>
      ) : (
        <ul className="space-y-1">
          {lines.map((line) => (
            <li
              key={line.code}
              className="flex items-baseline justify-between gap-2 rounded-md border border-line px-3 py-2 text-sm"
            >
              <AccountLink code={line.code} name={line.nameAr} />
              <span className="tabular" dir="ltr">{money(line.balance)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: 'ok' | 'warn' }) {
  return (
    <div className="rounded-md border border-line px-3 py-2">
      <span className="block text-xs text-ink-soft">{label}</span>
      <b
        className={
          'tabular block text-lg '
          + (tone === 'ok' ? 'text-ok' : tone === 'warn' ? 'text-warn' : '')
        }
        dir="ltr"
      >
        {value}
      </b>
    </div>
  )
}

function JournalsPanel({ range }: { range: string }) {
  const { can } = useAuth()
  const queries = useQueryClient()
  const [open, setOpen] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  const journals = useQuery({
    queryKey: ['journals', range],
    queryFn: () => api<Journal[]>(`/api/v1/accounting/journals?${range}`),
  })

  const reverse = useMutation({
    mutationFn: (id: string) =>
      api(`/api/v1/accounting/journals/${id}/reverse`, {
        method: 'POST',
        body: { reason: reason.trim() },
      }),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: ['journals'] })
      await queries.invalidateQueries({ queryKey: ['trial-balance'] })
      setOpen(null)
      setReason('')
    },
  })

  if (journals.isPending) {
    return <Spinner />
  }

  if (journals.isError) {
    return <ErrorBanner error={journals.error} />
  }

  const rows = journals.data ?? []

  return (
    <Panel title={`القيود · ${rows.length}`}>
      {rows.length === 0 ? (
        <Empty>لا قيودَ في هذا المدى.</Empty>
      ) : (
        <ul className="space-y-2">
          {rows.map((journal) => (
            <li key={journal.id} className="rounded-md border border-line px-3 py-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">
                  <span className="tabular" dir="ltr">{journal.journalNo}</span>
                  {' — '}
                  {SOURCE_NAMES[journal.sourceType] ?? journal.sourceType}
                </span>
                <span className="tabular text-sm" dir="ltr">{money(journal.total)}</span>
              </div>

              <div className="mt-1 text-xs text-ink-soft">
                <span dir="ltr">{journal.entryDate}</span>
                {journal.memo && <span> · {journal.memo}</span>}
                {journal.isReversed && <b> · معكوس</b>}
              </div>

              <table className="mt-2 w-full text-xs">
                <tbody>
                  {journal.lines.map((line) => (
                    <tr key={line.lineNo} className="border-t border-line">
                      <td className="p-1">
                        <span className="tabular" dir="ltr">{line.accountCode}</span>
                        {' '}{line.accountName}
                      </td>
                      <td className="tabular p-1 text-end" dir="ltr">
                        {line.debit > 0 ? money(line.debit) : '—'}
                      </td>
                      <td className="tabular p-1 text-end" dir="ltr">
                        {line.credit > 0 ? money(line.credit) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/*
                ⚠ **وقيدُ مستندٍ لا يُعكَس من هنا** — يُعكَس بعكسِ مستنده،
                  وإلا اختلف الدفترُ عن المستندات ولا يُعرف أيُّهما الصحيح.
              */}
              {journal.sourceType === 'MANUAL' && !journal.isReversed && can(Perm.journalReverse) && (
                <div className="mt-2">
                  {editing === journal.id ? (
                    <JournalAmend journal={journal} onDone={() => setEditing(null)} onCancel={() => setEditing(null)} />
                  ) : open === journal.id ? (
                    <div className="space-y-2">
                      <Field label="سببُ العكس">
                        <TextInput value={reason} onChange={setReason} />
                      </Field>

                      {reverse.isError && <ErrorBanner error={reverse.error} />}

                      <div className="flex gap-2">
                        <Button
                          tone="danger"
                          disabled={reason.trim().length === 0 || reverse.isPending}
                          onClick={() => reverse.mutate(journal.id)}
                        >
                          {reverse.isPending ? 'يُعكَس…' : 'تأكيدُ العكس'}
                        </Button>
                        <Button onClick={() => setOpen(null)}>تراجع</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      {can(Perm.journalPost) && (
                        <Button onClick={() => { setEditing(journal.id); setOpen(null) }}>عدّل</Button>
                      )}
                      <Button onClick={() => { setOpen(journal.id); setReason('') }}>عكسُ القيد</Button>
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}

/** قيدٌ يدويّ — <b>وهو سببُ وجود المحاسبة أصلًا</b>: ما لا يقع بمستند. */
function ManualEntry() {
  const queries = useQueryClient()
  const [memo, setMemo] = useState('')
  const [debitCode, setDebitCode] = useState('')
  const [creditCode, setCreditCode] = useState('')
  const [amount, setAmount] = useState('')

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api<Account[]>('/api/v1/accounting/accounts'),
    staleTime: 30 * 60_000,
  })

  const post = useMutation({
    mutationFn: () =>
      api('/api/v1/accounting/journals', {
        method: 'POST',
        body: {
          memo: memo.trim(),
          lines: [
            { accountCode: debitCode, debit: toNumber(amount) ?? 0, credit: 0 },
            { accountCode: creditCode, debit: 0, credit: toNumber(amount) ?? 0 },
          ],
        },
      }),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: ['journals'] })
      await queries.invalidateQueries({ queryKey: ['trial-balance'] })
      await queries.invalidateQueries({ queryKey: ['income'] })
      setMemo('')
      setAmount('')
    },
  })

  const value = toNumber(amount) ?? 0
  const ready =
    memo.trim().length > 0 && debitCode !== '' && creditCode !== ''
    && debitCode !== creditCode && value > 0 && !post.isPending

  const options = (accounts.data ?? []).map((a) => (
    <option key={a.id} value={a.code}>{a.code} — {a.nameAr}</option>
  ))

  return (
    <Panel title="قيدٌ يدويّ">
      <p className="mb-3 text-xs text-ink-soft">
        لِما لا يقع بمستندٍ في النظام: إيجارٌ وراتبٌ وكهرباءُ ومسحوبٌ شخصيّ.
        <b> والطرفان إلزاميّان</b> — من أين خرج المال وإلى أين ذهب.
      </p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="البيان">
          <TextInput value={memo} onChange={setMemo} placeholder="إيجارُ المحلّ لشهر ٩" />
        </Field>

        <Field label="مدين (إلى أين ذهب)">
          <Select value={debitCode} onChange={setDebitCode}>
            <option value="">اختر…</option>
            {options}
          </Select>
        </Field>

        <Field label="دائن (من أين خرج)">
          <Select value={creditCode} onChange={setCreditCode}>
            <option value="">اختر…</option>
            {options}
          </Select>
        </Field>

        <Field label="المبلغ">
          <NumberInput value={amount} onChange={setAmount} placeholder="250.000" />
        </Field>
      </div>

      {post.isError && (
        <div className="mt-3">
          <ErrorBanner error={post.error} />
        </div>
      )}

      {post.isSuccess && (
        <div className="mt-3">
          <Banner tone="ok">رُحّل القيد.</Banner>
        </div>
      )}

      <div className="mt-3">
        <Button tone="primary" disabled={!ready} onClick={() => post.mutate()}>
          {post.isPending ? 'يُرحَّل…' : 'رحِّل القيد'}
        </Button>
      </div>
    </Panel>
  )
}
